/**
 * Bot-Erkennung und Länder-Sperre.
 *
 * Reines, testbares Modul ohne Server-Abhängigkeiten. Wird im Request-Einstieg
 * (src/server.ts) ausgewertet, bevor SSR gerendert wird.
 */

export type TrafficClass = "human" | "known_good_bot" | "suspicious_bot";

/** Länder, die vollständig gesperrt sind (kein Markt für creategy). */
export const BLOCKED_COUNTRIES = new Set<string>(["CN"]);

/** Pfade, die trotz Länder-Sperre erreichbar bleiben müssen. */
const BLOCK_EXEMPT_PREFIXES = [
  "/lovable/",
  "/api/public/hooks/",
  "/email/unsubscribe",
  "/robots.txt",
];

/** Suchmaschinen und Social-Preview-Bots, die ausdrücklich erlaubt sind. */
const GOOD_BOT_PATTERNS: RegExp[] = [
  /googlebot/i,
  /google-inspectiontool/i,
  /adsbot-google/i,
  /google-site-verification/i,
  /storebot-google/i,
  /bingbot/i,
  /bingpreview/i,
  /duckduckbot/i,
  /applebot(?!-extended)/i,
  /ecosia/i,
  /qwantify/i,
  /facebookexternalhit/i,
  /twitterbot/i,
  /linkedinbot/i,
  /slackbot/i,
  /whatsapp/i,
  /telegrambot/i,
  /discordbot/i,
  /pinterest/i,
  /lighthouse/i,
  /chrome-lighthouse/i,
];

/**
 * Aggressive Scraper, KI-Crawler, SEO-Crawler, Scanner und generische
 * HTTP-Bibliotheken. Länderunabhängig.
 */
const SUSPICIOUS_BOT_PATTERNS: RegExp[] = [
  // KI-/LLM-Crawler
  /gptbot/i,
  /oai-searchbot/i,
  /chatgpt-user/i,
  /claudebot/i,
  /claude-web/i,
  /anthropic-ai/i,
  /perplexitybot/i,
  /perplexity-user/i,
  /ccbot/i,
  /google-extended/i,
  /applebot-extended/i,
  /meta-externalagent/i,
  /meta-externalfetcher/i,
  /amazonbot/i,
  /bytespider/i,
  /timpibot/i,
  /omgili/i,
  /diffbot/i,
  /imagesiftbot/i,
  // SEO-/Marketing-Crawler
  /ahrefsbot/i,
  /semrushbot/i,
  /mj12bot/i,
  /dotbot/i,
  /blexbot/i,
  /dataforseo/i,
  /serpstatbot/i,
  /seokicks/i,
  /megaindex/i,
  /petalbot/i,
  /sogou/i,
  /baiduspider/i,
  /yisouspider/i,
  /360spider/i,
  /bytedance/i,
  // Scanner / Security-Scans
  /zgrab/i,
  /masscan/i,
  /nmap/i,
  /nuclei/i,
  /censys/i,
  /expanse/i,
  /internetmeasurement/i,
  /paloaltonetworks/i,
  /shodan/i,
  // Generische HTTP-Clients und Automatisierung
  /python-requests/i,
  /python-httpx/i,
  /aiohttp/i,
  /scrapy/i,
  /^curl\//i,
  /\bcurl\/\d/i,
  /wget/i,
  /libwww-perl/i,
  /go-http-client/i,
  /okhttp/i,
  /java\/\d/i,
  /apache-httpclient/i,
  /node-fetch/i,
  /axios\//i,
  /got \(/i,
  /guzzlehttp/i,
  /headlesschrome/i,
  /phantomjs/i,
  /puppeteer/i,
  /playwright/i,
  /selenium/i,
  /http_request2/i,
  /zabbix/i,
  /crawler/i,
  /spider/i,
  /scraper/i,
];

export type RequestSignals = {
  userAgent: string | null;
  accept: string | null;
  acceptLanguage: string | null;
};

export type Classification = {
  classification: TrafficClass;
  reason: string | null;
};

export function isBlockedCountry(country: string | null | undefined): boolean {
  if (!country) return false;
  return BLOCKED_COUNTRIES.has(country.toUpperCase());
}

export function isBlockExemptPath(pathname: string): boolean {
  return BLOCK_EXEMPT_PREFIXES.some((p) => pathname.startsWith(p));
}

export function classifyRequest(signals: RequestSignals): Classification {
  const ua = (signals.userAgent ?? "").trim();

  if (ua.length === 0) {
    return { classification: "suspicious_bot", reason: "empty_user_agent" };
  }

  for (const pattern of GOOD_BOT_PATTERNS) {
    if (pattern.test(ua)) {
      return { classification: "known_good_bot", reason: pattern.source };
    }
  }

  for (const pattern of SUSPICIOUS_BOT_PATTERNS) {
    if (pattern.test(ua)) {
      return { classification: "suspicious_bot", reason: pattern.source };
    }
  }

  // Generisches "bot"/"http" im UA, das keiner Liste zuzuordnen ist.
  if (/\bbot\b|bot[\/;)]|https?:\/\//i.test(ua)) {
    return { classification: "suspicious_bot", reason: "generic_bot_token" };
  }

  // Echte Browser senden immer Accept und praktisch immer Accept-Language.
  if (!signals.accept) {
    return { classification: "suspicious_bot", reason: "missing_accept" };
  }
  if (!signals.acceptLanguage) {
    return { classification: "suspicious_bot", reason: "missing_accept_language" };
  }

  return { classification: "human", reason: null };
}

/** Nur Seitenaufrufe protokollieren – keine Assets, kein RUM-Ingest, keine RPCs. */
const LOG_SKIP_PREFIXES = [
  "/assets/",
  "/_serverFn/",
  "/api/public/rum",
  "/lovable/",
  "/@vite",
  "/@fs",
  "/node_modules/",
  "/src/",
];

const ASSET_EXTENSION = /\.(js|mjs|css|map|png|jpe?g|webp|avif|svg|ico|gif|woff2?|ttf|mp4|webm|txt|xml|json)$/i;

export function shouldLogPath(pathname: string): boolean {
  if (LOG_SKIP_PREFIXES.some((p) => pathname.startsWith(p))) return false;
  if (ASSET_EXTENSION.test(pathname)) return false;
  return true;
}
